// PromSection.js
import {
  defineComponent,
  ref,
  computed,
  watch,
  inject,
  onMounted,
  onUnmounted,
  nextTick,
  onBeforeUnmount,
} from "/Scripts/vue.esm-browser.js";
import {
  getTextWithoutSign,
  transformPresentImageUrl,
} from "/Scripts/tools.js";
const html = String.raw;

// === 等待 .topic_discount 出現 & 穩定 ===
export const runAfterTopicReady = async (opts = {}) => {
  const {
    selector = ".topic_discount", // 你要等的元素
    timeout = 12000, // 最長等待時間
    stableForMs = 180, // 尺寸在這段時間內不變才算穩定
    waitImages = true, // 是否等元素裡的圖片載完
  } = opts;

  if (typeof window === "undefined" || typeof document === "undefined") return;

  let el = null;
  try {
    el = await waitForEl(selector, timeout);
  } catch {
    /* 超時就略過 */
  }

  if (el && waitImages) await waitAllImages(el);
  if (el) await waitStable(el, stableForMs);

  await nextTick(); // 確保本輪 DOM 更新完成
};
const waitForEl = (selector, timeout = 10000) =>
  new Promise((resolve, reject) => {
    const exist = document.querySelector(selector);
    if (exist) return resolve(exist);

    const mo = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        cleanup();
        resolve(el);
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    const tid = setTimeout(() => {
      cleanup();
      reject(new Error("timeout"));
    }, timeout);
    function cleanup() {
      mo.disconnect();
      clearTimeout(tid);
    }
  });

const waitAllImages = (root) => {
  const imgs = Array.from(root.querySelectorAll("img")).filter(
    (img) => !img.complete || img.naturalWidth === 0,
  );
  if (!imgs.length) return Promise.resolve();
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise((res) => {
          img.addEventListener("load", () => res(), { once: true });
          img.addEventListener("error", () => res(), { once: true });
        }),
    ),
  ).then(() => undefined);
};

const waitStable = (el, stableForMs = 150) =>
  new Promise((resolve) => {
    if (typeof ResizeObserver === "undefined") {
      setTimeout(resolve, stableForMs);
      return;
    }
    let t = null;
    const ro = new ResizeObserver(() => {
      if (t) clearTimeout(t);
      t = setTimeout(done, stableForMs);
    });
    function done() {
      if (t) {
        clearTimeout(t);
        t = null;
      }
      ro.disconnect();
      resolve();
    }
    ro.observe(el);
    t = setTimeout(done, stableForMs); // 初始保底
  });

const getDiscountImageUrl = (url) => {
  if (!url) return "";
  if (url?.includes("/travel/")) {
    const match = url.match(/[?&]F=([^&]+)/);
    if (match && match[1]) {
      return `https://ncapi.bwt.com.tw/image/document/${match[1]}`;
    }
  }
  return transformPresentImageUrl(
    // url.replace("ncapi.bwt.com.tw", "ncapi.ibwt.com.tw"),
    url,
    { targetWidth: 288 },
  );
};

const getFormattedMinPrice = (groups = [], field) => {
  let minPrice = Infinity;
  groups.forEach((group) => {
    if (group[field] < minPrice) {
      minPrice = group[field];
    }
  });

  return Number.isFinite(minPrice)
    ? minPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : "";
};

export default defineComponent({
  name: "PromSection",
  props: {
    currentTab: {
      type: String,
    },
    promoData: {
      type: Array,
    },
    correspond: {
      type: Array,
    },
    prom: {
      type: String,
    },
  },
  emits: [],
  setup(props, { emit }) {
    const allProductPopup = inject("allProductPopup");
    const allProductBaseData = inject("allProductBaseData");
    const compareGroups = inject("compareGroups");
    const selectedIndex = ref(-1);
    const nowPage = inject("nowPage");
    const isOverflow = ref([]); // 二維陣列 [idx][groupIdx] = true/false
    const isCardStateOverflow = ref([]); // 二維陣列 [idx][groupIdx] = true/false
    const dateBoxes = ref([[]]);
    const cardStateDateBoxes = ref([[]]);
    const favorite = ($event) => {
      $event.stopPropagation();
      setTimeout(() => {
        UpdateClass();
      }, 2000);
    };
    props.promoData.forEach((prom, index) => {
      prom.isHide = prom.groups.length > 10 ? true : false;
    });

    const discountContainers = ref([]);
    const swiperInstance = ref(null);
    const resizeTimer = ref(null);

    const initializeOrDestroySwiper = () => {
      const windowWidth = window.innerWidth;

      discountContainers.value.forEach((container) => {
        const wrapper = container?.querySelector(".discount_wrap");
        if (!container || !wrapper) return;

        if (windowWidth > 850) {
          if (container.swiperInstance && container.swiperInstance.destroy) {
            container.swiperInstance.destroy(true, true);
            container.swiperInstance = null;
          }

          wrapper.classList.remove("swiper-wrapper");
          Array.from(wrapper.children).forEach((child) => {
            child.classList.remove("swiper-slide");
          });
          wrapper.style.transform = "translate3d(0, 0, 0)";
        } else {
          wrapper.classList.add("swiper-wrapper");
          Array.from(wrapper.children).forEach((child) => {
            child.classList.add("swiper-slide");
          });

          if (!container.swiperInstance) {
            container.swiperInstance = new Swiper(container, {
              slidesPerView: "auto",
              centeredSlides: false,
              slidesOffsetAfter: 20,
              slidesOffsetBefore: 20,
              resistanceRatio: 0,
              spaceBetween: 10,
              navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
              },
              pagination: {
                el: ".swiper-pagination",
                clickable: true,
              },
            });
          }
        }
      });
    };
    const getDateHtml = (groups) => {
      const groupedDates = {};

      // 先按照 leaveDate 排序，確保跨年時的順序正確
      groups
        .slice()
        .sort((a, b) => {
          const dateA = new Date(a.leaveDate.replace(/\//g, "-"));
          const dateB = new Date(b.leaveDate.replace(/\//g, "-"));
          return dateA - dateB;
        })
        .forEach((item) => {
          const [year, month, day] = item.leaveDate.split("/");
          const monthNum = parseInt(month, 10);
          const dayNum = parseInt(day, 10);
          const key = `${year}-${monthNum}`;

          if (!groupedDates[key]) {
            groupedDates[key] = new Set();
          }

          groupedDates[key].add(dayNum); // Set 會自動排除重複日期
        });

      const html = Object.keys(groupedDates)
        .map((date) => {
          const [year, month] = date.split("-");
          const days = [...groupedDates[date]].sort((a, b) => a - b).join(".");
          return `<mark>${month}月</mark>${days}`;
        })
        .join("、");

      return html;
    };
    const getEnsureDateHtml = (groups) => {
      // 分組：key = `${year}-${month}`，value = Set(天)
      const grouped = new Map();

      groups
        .slice()
        .filter((item) => item.hotType == 6 && item.leaveDate)
        // 先按日期排序，確保跨年月份順序正確
        .sort(
          (a, b) =>
            new Date(a.leaveDate.replace(/\//g, "-")) -
            new Date(b.leaveDate.replace(/\//g, "-")),
        )
        .forEach((item) => {
          const [year, month, day] = item.leaveDate.split("/");
          const key = `${year}-${parseInt(month, 10)}`;
          const dayNum = parseInt(day, 10);

          if (!grouped.has(key)) grouped.set(key, new Set());
          grouped.get(key).add(dayNum); // Set 會自動去重
        });

      // 輸出
      const html = Array.from(grouped.entries())
        .map(([key, daySet]) => {
          const [, month] = key.split("-");
          const days = Array.from(daySet)
            .sort((a, b) => a - b)
            .join(".");
          return `<mark>${month}月</mark>${days}`;
        })
        .join("、");

      return html;
    };

    const handleResize = () => {
      clearTimeout(resizeTimer.value);
      resizeTimer.value = setTimeout(() => {
        initializeOrDestroySwiper();
      }, 300);
    };
    const getName = computed(() => {
      return (subArea) => {
        if (!props.correspond || !props.correspond.length) return "";
        const found = props.correspond.find((item) => item.tvlArea == subArea);
        return found ? found.showName : "";
      };
    });
    const normalizedPromos = computed(() =>
      (props.promoData || []).map((promo) => ({
        ...promo,
        key: promo.promSn,
        groups: (promo.groups || []).map((group) => ({
          ...group,
          key: group.mainGroupCode,
          displayTitle: group.groupName.split("～")[0],
          displayDescription: group.groupName.split("～")[1],
          discountImg: getDiscountImageUrl(group.picture),
          ensureDateHtml: getEnsureDateHtml(group.groups),
          dateHtml: getDateHtml(group.groups),
          minPriceText: getFormattedMinPrice(group.groups, "price"),
          minSitePriceText: getFormattedMinPrice(group.groups, "sitePrice"),
          hashTags: group.hashTagList || [],
        })),
      })),
    );
    const promoRenderSignature = computed(() =>
      normalizedPromos.value
        .map((promo) => `${promo.promSn}:${promo.groups?.length ?? 0}`)
        .join("|"),
    );
    const checkOverflow = () => {
      nextTick(() => {
        dateBoxes.value.forEach((row, idx) => {
          // 確保每個 idx 的 overflow 陣列存在
          isOverflow.value[idx] = isOverflow.value[idx] || [];

          row.forEach((el, groupIdx) => {
            if (el) {
              const isOver = el.scrollWidth > el.clientWidth;
              isOverflow.value[idx][groupIdx] = isOver;
            }
          });
        });
        cardStateDateBoxes.value.forEach((row, idx) => {
          // 確保每個 idx 的 overflow 陣列存在
          isCardStateOverflow.value[idx] = isCardStateOverflow.value[idx] || [];

          row.forEach((el, groupIdx) => {
            if (el) {
              const isOver = el.scrollWidth > el.clientWidth;
              isCardStateOverflow.value[idx][groupIdx] = isOver;
            }
          });
        });
      });
    };
    const getHiddenElementHeight = (el) => {
      if (!el) return 0;

      const originalDisplay = el.style.display;
      const originalVisibility = el.style.visibility;
      const originalPosition = el.style.position;

      // 暫時顯示，且不讓它影響畫面
      el.style.visibility = "hidden";
      el.style.position = "absolute";
      el.style.display = "block";

      const height = el.offsetHeight;

      // 還原
      el.style.display = originalDisplay;
      el.style.visibility = originalVisibility;
      el.style.position = originalPosition;

      return height;
    };
    const scrollToAnchor = (id) => {
      const element = document.getElementById(id);
      if (element) {
        const headerEl = document.querySelector(".header_m");
        const navEl = document.querySelector(".destination_topnav");

        const headerHeight = headerEl ? headerEl.offsetHeight : 0;
        const navHeight = getHiddenElementHeight(navEl);

        const totalOffset = headerHeight + navHeight;

        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
          top: elementPosition - totalOffset,
          behavior: "smooth",
        });
      }
    };

    const setUrl = (sarea, oarea) => {
      const currentPath = window.location.pathname; // 例如 /destination/7
      const queryString = window.location.search; // 例如 ?weekdays=1

      // 使用正規表達式抓出 /destination/Sn 中的 Sn
      const match = currentPath.match(/^\/destination\/([^\/]+)/i);

      if (match) {
        const sn = match[1];
        let newPath = "";
        if (oarea) {
          newPath = `/destination/${sn}/${sarea}/${oarea}${queryString}`;
        } else {
          newPath = `/destination/${sn}/${sarea}${queryString}`;
        }

        window.history.pushState({}, "", newPath);
      } else {
        console.error("網址不符 /destination/Sn 格式");
      }
    };

    onMounted(async () => {
      // 先等本輪 DOM 更新
      await nextTick();

      // 等待 .topic_discount 出現、圖片載入完成、尺寸穩定
      await runAfterTopicReady({
        selector: ".topic_discount",
        timeout: 12000,
        stableForMs: 180,
        waitImages: true,
      });

      // 一次完整初始化流程
      initializeOrDestroySwiper();

      // Swiper / 其他元件初始化可能會改變版面，等一個 tick
      await nextTick();

      // 綁定事件、做版面依賴內容的檢查
      BindCompareButton(compareGroups.value);
      checkOverflow();

      // 這時候 anchor 位置已穩定，再捲動到目標
      if (props.prom) {
        scrollToAnchor(props.prom);
      }

      // 如果想更保險（避免初始化後又有微幅尺寸變動），再等一次穩定再捲一次
      const host = document.querySelector(".topic_discount");
      if (host) {
        await waitStable(host, 180);
        if (props.prom) {
          scrollToAnchor(props.prom);
        }
      }

      // 監聽視窗尺寸變動
      window.addEventListener("resize", handleResize);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
      discountContainers.value.forEach((container) => {
        if (container.swiperInstance && container.swiperInstance.destroy) {
          container.swiperInstance.destroy(true, true);
        }
      });
    });
    watch(promoRenderSignature, () => {
      setTimeout(() => {
        initializeOrDestroySwiper();
        checkOverflow();
      }, 500); // 小延遲，確保 HTML 插入完畢
    });

    return {
      allProductPopup,
      allProductBaseData,
      nowPage,
      favorite,
      discountContainers,
      getName,
      getDateHtml,
      getEnsureDateHtml,
      dateBoxes,
      cardStateDateBoxes,
      isOverflow,
      isCardStateOverflow,
      setUrl,
      scrollToAnchor,
      normalizedPromos,
    };
  },
  template: html`
    <div
      class="discount_campaign_wrap layout layout-1"
      :class="{ active: currentTab === 'prom' }"
      :id="'prom'+promo.promSn"
      v-for="(promo,idx) in normalizedPromos"
      :key="promo.key"
    >
      <div class="discount_campaign_header">
        <div
          class="discount_campaign_title"
          @click="scrollToAnchor('prom'+promo.promSn);setUrl('prom'+promo.promSn,'');"
        >
          {{promo.prom}}
        </div>
        <div class="depart_popup_toggle" @click="openAllProductPopup(promo)">
          <span>所有出發日期</span>
          <i class="fa-solid fa-circle-chevron-right"></i>
        </div>
      </div>
      <div
        class="discount_container slider-discount slider"
        :ref="el => el && discountContainers.push(el)"
      >
        <div class="discount_wrap">
          <div
            class="discount_card"
            v-for="(group,groupIdx) in promo.groups"
            :key="group.key"
            @click="goToLink(group.mainGroupCode)"
          >
            <img
              loading="lazy"
              :src="'/Content/Img/Shop/SAREA/item_location-'+group.subArea+'.png'"
              class="discount_decor_txt"
            />
            <div class="discount_location">{{getName(group.subArea)}}</div>
            <div class="discount_img_wrap">
              <div class="discount_img">
                <picture>
                  <source :srcset="group.discountImg+'/.avif'" />
                  <source :srcset="group.discountImg" />
                  <img
                    loading="lazy"
                    decoding="async"
                    src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
                  />
                </picture>
              </div>
            </div>
            <div class="discount_txt_wrap">
              <div class="card_info">
                <div
                  class="discount_title"
                  :class="{ 'text-indent': (group.groupName||'').startsWith('【') }"
                >
                  {{group.displayTitle}}
                </div>
                <div class="discount_describe">
                  {{group.displayDescription}}
                </div>
                <ul class="product_label" v-if="group.hashTags.length>0">
                  <li v-for="tag in group.hashTags" :key="tag">{{tag}}</li>
                </ul>
              </div>
              <div class="card_discount_wrap">
                <div class="card_state" v-if="group.ensureDateHtml">
                  <svg
                    class="icon-state"
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <mask
                      id="mask0_1368_134"
                      style="mask-type:alpha"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="19"
                      height="19"
                    >
                      <rect width="19" height="19" fill="#D9D9D9"></rect>
                    </mask>
                    <g mask="url(#mask0_1368_134)">
                      <path
                        d="M16.625 6.33281C17.0472 6.33281 17.4167 6.49114 17.7333 6.80781C18.05 7.12448 18.2083 7.49392 18.2083 7.91614V9.49948C18.2083 9.59184 18.1984 9.6908 18.1787 9.79635C18.1589 9.90191 18.1292 10.0009 18.0896 10.0932L15.7146 15.6745C15.5958 15.9384 15.3979 16.1627 15.1208 16.3474C14.8438 16.5321 14.5535 16.6245 14.25 16.6245H7.91668C7.48126 16.6245 7.10852 16.4694 6.79845 16.1594C6.48838 15.8493 6.33334 15.4766 6.33334 15.0411V6.98594C6.33334 6.77482 6.37623 6.57361 6.46199 6.38229C6.54775 6.19097 6.6632 6.02274 6.80834 5.8776L11.1031 1.6026C11.3011 1.41788 11.5353 1.30573 11.8057 1.26614C12.0762 1.22656 12.3368 1.27274 12.5875 1.40469C12.8382 1.53663 13.0196 1.72135 13.1318 1.95885C13.2439 2.19635 13.267 2.44045 13.2011 2.69114L12.3104 6.33281H16.625ZM3.16668 16.6245C2.73126 16.6245 2.35852 16.4694 2.04845 16.1594C1.73838 15.8493 1.58334 15.4766 1.58334 15.0411V7.91614C1.58334 7.48073 1.73838 7.10798 2.04845 6.79791C2.35852 6.48784 2.73126 6.33281 3.16668 6.33281C3.60209 6.33281 3.97484 6.48784 4.28491 6.79791C4.59498 7.10798 4.75001 7.48073 4.75001 7.91614V15.0411C4.75001 15.4766 4.59498 15.8493 4.28491 16.1594C3.97484 16.4694 3.60209 16.6245 3.16668 16.6245Z"
                        fill="#D00003"
                      ></path>
                    </g>
                  </svg>
                  <span class="dateText">
                    <span
                      class="dateText_box"
                      :ref="el => {
                            if (!cardStateDateBoxes[idx]) {
                                cardStateDateBoxes[idx] = [];
                            }
                            cardStateDateBoxes[idx][groupIdx] = el;
                        }"
                    >
                      <span>
                        <mark>保證出發：</mark>
                        <span v-html="group.ensureDateHtml"> </span>
                      </span>
                    </span>
                    <div
                      class="card_date_more_txt"
                      v-if="isCardStateOverflow[idx] && isCardStateOverflow[idx][groupIdx]"
                    >
                      ...
                    </div>
                  </span>
                </div>
                <div class="card_discount" v-if="group.discount">
                  <svg
                    class="icon-discount_gift"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_7471_58273)">
                      <path
                        d="M6.12787 0.58619C6.19527 0.628836 6.2625 0.671753 6.32955 0.714957C6.56604 0.833739 6.66883 0.820045 6.92574 0.770587C7.30428 0.708581 7.67337 0.685613 8.05694 0.68548C8.11031 0.685316 8.16368 0.685151 8.21866 0.684982C8.50216 0.687441 8.76744 0.70965 9.04547 0.763633C9.60636 0.870676 9.92216 0.688021 10.3927 0.415866C11.0233 0.0724135 11.8931 -0.0953636 12.5984 0.0558261C13.1122 0.20757 13.5509 0.476311 13.8444 0.934372C14.1148 1.4401 14.1308 1.95975 14.0144 2.51527C14.0665 2.51438 14.1186 2.51348 14.1722 2.51257C14.366 2.50966 14.5597 2.50753 14.7534 2.50592C14.8372 2.50506 14.9209 2.5039 15.0047 2.50243C15.1252 2.50036 15.2458 2.4994 15.3663 2.49865C15.4751 2.49731 15.4751 2.49731 15.586 2.49594C15.7922 2.51723 15.8568 2.53994 16.0002 2.68548C16.0286 2.83835 16.0286 2.83835 16.0287 3.02502C16.0295 3.12982 16.0295 3.12982 16.0303 3.23673C16.0297 3.31303 16.029 3.38933 16.0284 3.46562C16.0284 3.54384 16.0284 3.62206 16.0286 3.70027C16.0287 3.86416 16.028 4.02802 16.0267 4.19191C16.0251 4.40198 16.0252 4.61201 16.0259 4.82208C16.0263 4.98359 16.0259 5.14508 16.0252 5.30658C16.0249 5.38404 16.0249 5.46149 16.0251 5.53895C16.0253 5.64719 16.0242 5.75543 16.0231 5.86367C16.0228 5.92527 16.0225 5.98687 16.0222 6.05034C16.0002 6.20321 16.0002 6.20321 15.9392 6.29721C15.7712 6.41445 15.6236 6.38961 15.4222 6.38406C15.35 6.38253 15.2778 6.38099 15.2034 6.37941C15.1204 6.37645 15.1204 6.37645 15.0357 6.37342C15.0358 6.446 15.0358 6.446 15.036 6.52004C15.0385 7.69624 15.0404 8.87244 15.0415 10.0486C15.0421 10.6174 15.0429 11.1862 15.0442 11.755C15.0454 12.2507 15.0461 12.7463 15.0463 13.242C15.0465 13.5045 15.0468 13.7671 15.0477 14.0296C15.0484 14.2766 15.0487 14.5235 15.0485 14.7705C15.0485 14.8613 15.0488 14.952 15.0492 15.0428C15.0498 15.1665 15.0496 15.2902 15.0493 15.4138C15.0495 15.5178 15.0495 15.5178 15.0496 15.6238C15.0357 15.7919 15.0357 15.7919 14.9222 15.9621C14.8202 15.9833 14.8202 15.9833 14.6941 15.9833C14.6461 15.9837 14.5981 15.9842 14.5487 15.9846C14.4957 15.9842 14.4427 15.9837 14.388 15.9833C14.332 15.9835 14.2759 15.9838 14.2181 15.984C14.0289 15.9845 13.8398 15.9839 13.6507 15.9832C13.5156 15.9833 13.3805 15.9836 13.2454 15.9838C12.9158 15.9843 12.5862 15.984 12.2566 15.9833C11.8731 15.9825 11.4895 15.9826 11.106 15.9827C10.4219 15.9829 9.73771 15.9823 9.05356 15.9812C8.38915 15.9801 7.72474 15.9797 7.06032 15.98C6.33745 15.9803 5.61458 15.9803 4.89171 15.9797C4.81468 15.9796 4.73766 15.9795 4.66063 15.9795C4.62272 15.9794 4.58482 15.9794 4.54577 15.9794C4.2782 15.9791 4.01063 15.9792 3.74306 15.9793C3.38317 15.9794 3.02329 15.9789 2.6634 15.978C2.53097 15.9778 2.39853 15.9777 2.2661 15.9779C2.08601 15.9781 1.90593 15.9776 1.72584 15.9769C1.67282 15.9771 1.61981 15.9773 1.56518 15.9775C1.5172 15.9772 1.46921 15.9769 1.41978 15.9765C1.37817 15.9765 1.33656 15.9764 1.2937 15.9764C1.1917 15.9621 1.1917 15.9621 1.07822 15.8486C1.02094 15.6397 1.01436 15.4408 1.01428 15.225C1.01412 15.1567 1.01396 15.0885 1.01379 15.0182C1.01391 14.9438 1.01402 14.8695 1.01414 14.7928C1.01406 14.7133 1.01396 14.6337 1.01384 14.5542C1.01358 14.3384 1.01368 14.1225 1.01386 13.9067C1.01401 13.6799 1.01387 13.4531 1.01378 13.2263C1.01368 12.8447 1.01382 12.4631 1.01409 12.0816C1.01439 11.6427 1.0143 11.2039 1.01398 10.765C1.01372 10.3868 1.01369 10.0087 1.01384 9.6305C1.01392 9.40529 1.01394 9.18008 1.01375 8.95487C1.01244 7.15744 1.01244 7.15744 1.07822 6.37342C1.01947 6.37614 0.960724 6.37886 0.900195 6.38167C0.479816 6.40113 0.479816 6.40113 0.283894 6.37342C0.139116 6.22992 0.0649042 6.13443 0.0288882 5.93222C0.0290499 5.87507 0.0292117 5.81793 0.0293783 5.75905C0.0288546 5.66135 0.0288546 5.66135 0.0283203 5.56168C0.0291357 5.49188 0.0299511 5.42207 0.0307912 5.35015C0.0308432 5.27805 0.0308951 5.20595 0.0309487 5.13166C0.0312913 4.97935 0.0320624 4.82712 0.0335892 4.67483C0.0355191 4.47991 0.035951 4.28502 0.0358649 4.09009C0.0359032 3.90372 0.0369064 3.71737 0.0378834 3.531C0.0380096 3.46108 0.0381359 3.39116 0.038266 3.31912C0.03899 3.25417 0.0397139 3.18922 0.0404598 3.1223C0.0408924 3.0652 0.0413249 3.0081 0.0417706 2.94927C0.0573661 2.79477 0.0863094 2.70167 0.170419 2.572C0.338255 2.48809 0.508275 2.50835 0.692139 2.50973C0.732277 2.50981 0.772416 2.50989 0.813771 2.50997C0.942112 2.51029 1.07045 2.511 1.19879 2.51172C1.28574 2.51201 1.37269 2.51226 1.45965 2.5125C1.67293 2.51312 1.88621 2.5141 2.0995 2.51527C2.08581 2.46339 2.08581 2.46339 2.07185 2.41046C1.95411 1.91606 2.04977 1.40246 2.28611 0.95986C3.16182 -0.440345 4.86764 -0.0425383 6.12787 0.58619ZM3.11014 1.16775C2.87652 1.52221 2.81457 1.79753 2.86435 2.22028C2.88981 2.36046 2.88981 2.36046 3.0073 2.51527C3.12616 2.54007 3.12616 2.54007 3.26497 2.53459C3.31835 2.53458 3.37173 2.53457 3.42673 2.53455C3.51335 2.53323 3.51335 2.53323 3.60171 2.53189C3.66074 2.53165 3.71977 2.53141 3.78059 2.53116C3.96965 2.5302 4.15867 2.52807 4.34772 2.52591C4.47568 2.52505 4.60363 2.52428 4.73159 2.52358C5.04573 2.5217 5.35984 2.51885 5.67396 2.51527C5.67912 2.42191 5.67912 2.42191 5.68438 2.32666C5.68917 2.24414 5.69398 2.16162 5.69879 2.0791C5.70102 2.03818 5.70325 1.99726 5.70555 1.95509C5.71833 1.73983 5.73799 1.53447 5.78744 1.32378C4.99608 0.897024 3.84847 0.408318 3.11014 1.16775ZM10.3264 1.32378C10.3352 1.36086 10.3439 1.39794 10.3528 1.43615C10.3908 1.6503 10.4033 1.86216 10.4151 2.0791C10.4175 2.12112 10.42 2.16313 10.4225 2.20642C10.4285 2.30936 10.4342 2.41231 10.4399 2.51527C10.8085 2.51814 11.1771 2.52013 11.5457 2.5215C11.6711 2.52207 11.7965 2.52285 11.9219 2.52383C12.1021 2.5252 12.2822 2.52585 12.4623 2.52635C12.5466 2.52723 12.5466 2.52723 12.6326 2.52812C12.6849 2.52813 12.7371 2.52814 12.7909 2.52815C12.8369 2.5284 12.8829 2.52865 12.9303 2.52891C13.067 2.52253 13.067 2.52253 13.2201 2.40179C13.332 2.02701 13.2779 1.6691 13.1066 1.32378C12.9475 1.06636 12.7599 0.930691 12.4825 0.81314C11.7358 0.698407 10.9605 0.936903 10.3264 1.32378ZM6.46829 1.77768C6.43201 1.89872 6.43201 1.89872 6.44701 2.02945C6.44892 2.07268 6.45082 2.1159 6.45278 2.16044C6.46778 2.30619 6.46778 2.30619 6.52503 2.51527C7.53609 2.51527 8.54716 2.51527 9.58886 2.51527C9.64305 2.3527 9.6603 2.23129 9.66687 2.06137C9.66965 2.01222 9.67243 1.96307 9.67529 1.91243C9.65793 1.76722 9.65793 1.76722 9.52658 1.68083C8.72774 1.32495 7.19388 1.19937 6.46829 1.77768ZM0.85127 3.30959C0.85127 4.07725 0.85127 4.84491 0.85127 5.63583C2.33042 5.63583 3.80957 5.63583 5.33354 5.63583C5.33354 4.86818 5.33354 4.10052 5.33354 3.30959C3.85439 3.30959 2.37524 3.30959 0.85127 3.30959ZM6.1846 3.30959C6.1846 4.07725 6.1846 4.84491 6.1846 5.63583C7.42035 5.63583 8.65609 5.63583 9.92928 5.63583C9.92928 4.86818 9.92928 4.10052 9.92928 3.30959C8.69354 3.30959 7.45779 3.30959 6.1846 3.30959ZM10.7803 3.30959C10.7803 4.07725 10.7803 4.84491 10.7803 5.63583C12.2408 5.63583 13.7012 5.63583 15.2059 5.63583C15.2059 4.86818 15.2059 4.10052 15.2059 3.30959C13.7455 3.30959 12.285 3.30959 10.7803 3.30959ZM1.87255 6.37342C1.87255 9.29427 1.87255 12.2151 1.87255 15.2245C3.22063 15.2245 4.56872 15.2245 5.95765 15.2245C5.97638 12.3036 5.9951 9.38278 6.01439 6.37342C4.64758 6.37342 3.28077 6.37342 1.87255 6.37342ZM6.80872 6.37342C6.80872 9.29427 6.80872 12.2151 6.80872 15.2245C7.61382 15.2245 8.41893 15.2245 9.24843 15.2245C9.26716 12.3036 9.28588 9.38278 9.30517 6.37342C8.48134 6.37342 7.65751 6.37342 6.80872 6.37342ZM10.0995 6.37342C10.0995 9.29427 10.0995 12.2151 10.0995 15.2245C11.4663 15.2245 12.8331 15.2245 14.2413 15.2245C14.2413 12.3036 14.2413 9.38278 14.2413 6.37342C12.8745 6.37342 11.5077 6.37342 10.0995 6.37342Z"
                        fill="#123860"
                      ></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_7471_58273">
                        <rect width="16" height="16" fill="white"></rect>
                      </clipPath>
                    </defs>
                  </svg>
                  {{group.discount}}
                </div>
                <div class="card_keywords" v-if="group.tag">
                  <svg
                    class="icon-discount_keywords"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="18"
                    viewBox="0 0 16 18"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_1375_17194)">
                      <path
                        d="M6.25028 13.1667H10.7497M7.00018 15.5H9.99977M6.25028 10.8333C6.25103 9.27778 5.87609 8.88889 5.12544 8.11111C4.37479 7.33333 4.01934 6.54544 4.00059 5.38889C3.96535 3.01667 5.50039 1.5 8.49997 1.5C11.5003 1.5 13.0361 3.01667 12.9994 5.38889C12.9821 6.54544 12.6244 7.33333 11.8745 8.11111C11.1254 8.88889 10.7504 9.27778 10.7497 10.8333"
                        stroke="#C4640D"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_1375_17194">
                        <rect
                          width="16"
                          height="16.99"
                          fill="white"
                          transform="translate(0 0.503906)"
                        ></rect>
                      </clipPath>
                    </defs>
                  </svg>

                  {{group.tag}}
                </div>
                <div class="card_date">
                  <svg
                    class="icon-discount_date"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                  >
                    <path
                      d="M13.8333 2.15385H11.5V1H10.3333V2.15385H5.66667V1H4.5V2.15385H2.16667C1.525 2.15385 1 2.67308 1 3.30769V14.8462C1 15.4808 1.525 16 2.16667 16H13.8333C14.475 16 15 15.4808 15 14.8462V3.30769C15 2.67308 14.475 2.15385 13.8333 2.15385ZM13.8333 14.8462H2.16667V6.76923H13.8333V14.8462ZM13.8333 5.61538H2.16667V3.30769H4.5V4.46154H5.66667V3.30769H10.3333V4.46154H11.5V3.30769H13.8333V5.61538Z"
                      fill="#6D6D6D"
                    ></path>
                  </svg>
                  <span class="dateText">
                    <span
                      class="dateText_box"
                      :ref="el => {
                                            if (!dateBoxes[idx]) {
                                                dateBoxes[idx] = [];
                                            }
                                            dateBoxes[idx][groupIdx] = el;
                                        }"
                    >
                      <span v-html="group.dateHtml"> </span>
                    </span>
                    <div
                      class="card_date_more_txt"
                      v-if="isOverflow[idx] && isOverflow[idx][groupIdx]"
                    >
                      ...
                    </div>
                  </span>
                </div>
              </div>
              <div class="product_price_wrap">
                <div class="price_group">
                  <div class="price">{{group.minSitePriceText}}</div>
                  <div class="price_2">{{group.minPriceText}}</div>
                </div>
                <div
                  :id="'prom'+group.mainGroupCode"
                  class="favro fav-btn compare_0"
                  name="compare"
                  :grup_cd="group.mainGroupCode"
                  @click="favorite($event)"
                >
                  <svg
                    class="icon-favro"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                  >
                    <path
                      d="M3.52051 1.25488C4.85621 0.699639 6.1397 0.862288 7.35449 1.69141V1.69238C7.84078 2.02699 8.25402 2.4377 8.60645 2.91016L8.75391 3.11719H8.75488C8.78234 3.15714 8.81414 3.19916 8.83008 3.2207L8.86426 3.26953C9.11064 3.70513 9.5161 3.99217 9.99414 3.99121C10.4704 3.99006 10.8783 3.7028 11.1318 3.28223L11.1328 3.2832C11.2653 3.06686 11.4238 2.85592 11.5996 2.65039L11.7812 2.44727V2.44629C12.3977 1.77302 13.0983 1.28186 13.9434 1.07715L14.1143 1.04004H14.1172C15.5253 0.762647 16.7365 1.12439 17.7822 2.03223L17.9883 2.2207H17.9893C18.9529 3.14203 19.4865 4.42292 19.498 5.73633L19.4951 5.74316L19.4941 5.8623C19.4803 7.49979 18.9238 8.97227 18.0078 10.3613L17.8203 10.6377C16.8933 11.965 15.7399 13.0947 14.4521 14.1191L13.8926 14.5518C12.664 15.4748 11.357 16.285 9.99805 17.0361C8.38709 16.1403 6.84614 15.1686 5.4375 14.0312L4.8291 13.5225C3.50646 12.37 2.35878 11.1093 1.54102 9.61816L1.38184 9.31641C0.793702 8.14759 0.448014 6.96669 0.505859 5.73633L0.523438 5.49023V5.4873C0.69258 3.5092 1.62758 2.04289 3.51953 1.25391L3.52051 1.25488Z"
                      stroke="#736357"
                    ></path>
                  </svg>

                  <span>
                    加入
                    <br />
                    最愛
                  </span>
                </div>
              </div>
              <!--                            <div class="card_date_wrap">-->
              <!--                                <div class="card_date_txt">-->
              <!--                                    {{getDateInterval(group.groups)}}-->
              <!--                                </div>-->
              <!--                            </div>-->
            </div>
          </div>
          <div class="allproduct_discount">
            <div
              class="allproduct_discount_txt"
              @click="openAllProductPopup(promo)"
            >
              查看完整行程<img src="/Content/Img/Shop/arrow_icon.png" alt="" />
            </div>
            <div class="allproduct_discount_decor">
              <img src="/Content/Img/Shop/discount_decor.png" alt="" />
            </div>
          </div>
        </div>
        <div
          class="topic_moreproduct_toggle"
          :style="promo.isHide ? { display: 'flex' } : {}"
        >
          <input type="radio" :id="'topic_moreproduct_btn-'+(idx+1)" hidden />
          <label
            class="topic_moreproduct_btn"
            type="radio"
            :for="'topic_moreproduct_btn-'+(idx+1)"
            @click="promo.isHide=false;"
            >顯示全部</label
          >
        </div>
      </div>
    </div>
  `,
  methods: {
    getMinPrice: function (group) {
      let minPrice = Infinity;
      group.forEach((item) => {
        if (item.price < minPrice) {
          minPrice = item.price;
        }
      });
      //轉換成三位一撇
      minPrice = minPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return minPrice;
    },
    getMinSitePrice: function (group) {
      let minPrice = Infinity;
      group.forEach((item) => {
        if (item.sitePrice < minPrice) {
          minPrice = item.sitePrice;
        }
      });
      //轉換成三位一撇
      minPrice = minPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return minPrice;
    },
    getDateInterval: function (group) {
      if (!group || group.length === 0) return "";

      // 將 leaveDate 字串轉換為 Date 物件
      const dates = group
        .map((g) => new Date(g.leaveDate))
        .filter((d) => !isNaN(d)); // 避免無效日期

      if (dates.length === 0) return "";

      // 排序日期
      dates.sort((a, b) => a - b);

      const format = (date) => {
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${mm}/${dd}`;
      };

      const minDate = format(dates[0]);
      const maxDate = format(dates[dates.length - 1]);

      // 如果只有一筆就只回傳單一日期
      return minDate === maxDate ? minDate : `${minDate}~${maxDate}`;
    },
    goToLink: function (mgrupCd) {
      // 另開分頁
      window.open(`/tour/${mgrupCd}`, "_blank");
    },
    openAllProductPopup: function (data) {
      this.allProductPopup = true;
      this.allProductBaseData = {
        data: data.groups,
        subArea: data.prom,
        key: data.promSn,
        type: "prom",
      };
      this.nowPage = 1;
    },
    getDiscountImg: function (url) {
      if (url?.includes("/travel/")) {
        const match = url.match(/[?&]F=([^&]+)/); // 抓取 F= 後面的參數
        if (match && match[1]) {
          return `https://ncapi.bwt.com.tw/image/document/${match[1]}`;
        }
      }
      return transformPresentImageUrl(
        // url.replace("ncapi.bwt.com.tw", "ncapi.ibwt.com.tw"),
        url,
        { targetWidth: 288 },
      );
    },
  },
});
